import React, { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { tableCellRender, stringFilterOperators, isSingleSelectOperators } from "../util/tableUtils";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";

import { useGridApiRef } from "@mui/x-data-grid-pro";
import httpClient from "../../lib/httpClient";

export const CoverageList = (props) => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const { policyId, lastUpdate } = props;
  const [yearLookupValues, setYearLookupValues] = useState({});
  const [tableColumns, setTableColumns] = useState(null);
  const [guarantees, setGuarantees] = useState([]);
  const [guaranteesFetching, setGuaranteesFetching] = useState(false);

  const customTableCellRender = (rowDataValue) => {
    const tableCellRenderValue = tableCellRender(rowDataValue);
    return tableCellRenderValue === "0" ? "-" : tableCellRenderValue;
  };

  useEffect(() => {
    setGuaranteesFetching(true);
    if (policyId) {
      httpClient()
        .get(`/climatedb/${policyId}/guarantees`)
        .then((res) => {
          setGuarantees(res.data);
          setGuaranteesFetching(false);

          const allYearValues = res.data
            .map((policy) => policy.referenceDate)
            .sort()
            .reverse();
          const allYearValuesUnique = new Set(allYearValues);

          const yearsFilterValues = Array.from(allYearValuesUnique).map((year) => ({
            value: year,
            label: year,
          }));
          setYearLookupValues(yearsFilterValues);
        })
        .catch((err) => console.log(err));
    }
  }, [policyId]);

  useEffect(() => {
    const coverageListColumns = [
      {
        headerName: t("coverage.year"),
        field: "referenceDate",
        type: "singleSelect",
        filterable: true,
        filterOperators: isSingleSelectOperators,
        valueOptions: yearLookupValues,
      },
      {
        headerName: t("coverage.perilCode"),
        field: "perilGroup",
      },
      {
        headerName: t("coverage.peril"),
        width: 200,
        field: "perilName",
        filterable: true,
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.field || !filterItem.value || !filterItem.operator) {
            return null;
          }

          return (params) => {
            return params.value.toLowerCase().includes(filterItem.value.toLowerCase());
          };
        },
      },
      {
        headerName: t("coverage.postrenewalValueFinal"),
        field: "postrenewalValueFinal",
      },
      {
        headerName: t("coverage.premium"),
        field: "premium",
        filterable: false,
      },
      {
        headerName: t("coverage.compensationLimit"),
        field: "compesationLimitMax",
      },
      {
        headerName: t("coverage.compensationLimitPercentage"),
        field: "compesationLimitMaxPercentage",
      },
      {
        headerName: t("coverage.fixedExcess"),
        field: "fixedExcess",
      },
      {
        headerName: t("coverage.fixedExcessPercentage"),
        field: "fixedExcessPercentage",
      },
      {
        headerName: t("coverage.fixedExcessMin"),
        field: "fixedExcessMin",
      },
      {
        headerName: t("coverage.fixedExcessMax"),
        field: "fixedExcessMax",
      },
      {
        headerName: t("coverage.excess"),
        field: "excessPercentage",
      },
      {
        headerName: t("coverage.excessMin"),
        field: "excessMin",
      },
      {
        headerName: t("coverage.excessMax"),
        field: "excessMax",
      },
    ];

    const columns = coverageListColumns.map((tableColumn) => ({
      valueFormatter: (params) => customTableCellRender(params.value),
      filterOperators: stringFilterOperators,
      filterable: false,
      ...tableColumn,
    }));
    setTableColumns(columns);

    if (yearLookupValues[0]?.value) {
      apiRef.current.setFilterModel({
        items: [
          {
            field: "referenceDate",
            operator: "is",
            value: yearLookupValues[0]?.value,
            id: Math.floor(Math.random() * 1000),
          },
        ],
      });
    }
  }, [yearLookupValues, apiRef, t]);

  if (!tableColumns) {
    return null;
  }

  return (
    <Box pt={1}>
      <Paper>
        <Box p={2} pt={2}>
          <Typography
            variant={"h2"}
            style={{
              fontSize: "1.3rem",
              fontWeight: 400,
            }}
          >
            {t("coverage.title")}
          </Typography>
        </Box>
        <div style={{ maxHeight: "40vw", maxWidth: "100%", overflow: "auto" }}>
          <DataGridCystellarTable
            columns={tableColumns}
            rows={guarantees}
            rowCount={guarantees.length}
            getRowId={(rowData) =>
              `${rowData._id.policyNumber}-${rowData._id.perilGroup}-${rowData._id.perilCode}-${rowData._id.referenceDate}`
            }
            pagination={false}
            loading={guaranteesFetching}
            apiRef={apiRef}
            customToolbarComp={true}
            customToolbarCompMsg={lastUpdate}
            customNoRowsMsg={t("policy.emptyCoverageList")}
          />
        </div>
      </Paper>
    </Box>
  );
};
